/**
 * BoardProvider — 게시판 모듈 상태 Provider
 *
 * AppLayout 레벨에서 감싸져서 LeftMenuBoard와 BoardPage가
 * 동일한 Context를 공유합니다.
 */
import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { Trans, t } from '@lingui/macro';
import { useLocation, useNavigate } from 'react-router-dom';
import { BoardContext } from '../../context/BoardContext';
import { Board, Post } from '../../models/Board';
import { fetchBoards, fetchPosts, fetchPost, searchPosts as searchPostsService, getPinnedBoardIds, toggleBoardPin } from '../../services/BoardService';
import { useAppUIContext } from '../../context/AppUIContext';
import { My } from '../../services/My';
import { decodeIdFromUrl, buildBoardUrl } from './boardUrl';

interface Props {
  children: React.ReactNode;
}

const BoardProvider: React.FC<Props> = ({ children }) => {
  const { activeWorkspaceId, setActiveWorkspaceId, activeModule, workspaces } = useAppUIContext();

  // 새로고침 등으로 activeWorkspaceId가 null인데 workspaces가 있으면 자동 선택
  useEffect(() => {
    if (!activeWorkspaceId && workspaces.length > 0) {
      setActiveWorkspaceId(workspaces[0].id);
    }
  }, [activeWorkspaceId, workspaces]);

  const navigate = useNavigate();
  const location = useLocation();

  const [boards, setBoards] = useState<Board[]>([]);
  const [selectedBoard, setSelectedBoardRaw] = useState<Board | null>(null);
  const [selectedPost, setSelectedPostRaw] = useState<Post | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);

  // URL 구조: /board, /board/:boardId, /board/:boardId/:postId
  // URL 의 ID 는 하이픈 없는 hex32 로 노출 (boardUrl.ts 의 encode/decode 사용).
  // URL 이 source-of-truth. 호출처는 setSelectedBoard / setSelectedPost wrapper 로 navigate 만 호출,
  // 실제 state 는 아래 useEffect 가 URL 변경을 감지해서 갱신. → 뒤로가기/새로고침/직접URL 모두 동작.
  const urlIds = useMemo(() => {
    const m = location.pathname.match(/^\/board\/?([^/]+)?\/?([^/]+)?\/?$/);
    return {
      boardId: decodeIdFromUrl(m?.[1] ?? null),
      postId:  decodeIdFromUrl(m?.[2] ?? null),
    };
  }, [location.pathname]);

  /** 보드 선택 wrapper — URL push 만, state 는 URL→state useEffect 에서 갱신 */
  const setSelectedBoard = useCallback((board: Board | null) => {
    navigate(buildBoardUrl(board?.id));
  }, [navigate]);

  /** 게시글 선택 wrapper — URL push 만, state 는 URL→state useEffect 에서 갱신 */
  const setSelectedPost = useCallback((post: Post | null) => {
    if (!post) {
      const bId = selectedBoard?.id ?? urlIds.boardId;
      navigate(buildBoardUrl(bId));
    } else {
      navigate(buildBoardUrl(post.board_id, post.id));
    }
  }, [navigate, selectedBoard?.id, urlIds.boardId]);
  const [totalPosts, setTotalPosts] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  // 즐겨찾기(핀) 게시판 — 좌측 메뉴와 대시보드가 공유. 한 곳에서 토글하면 양쪽 즉시 반영.
  const [pinnedIds, setPinnedIds] = useState<string[]>([]);
  const userId = My.user?.id;
  useEffect(() => {
    if (!userId) { setPinnedIds([]); return; }
    getPinnedBoardIds(userId).then(setPinnedIds).catch(() => {});
  }, [userId, activeWorkspaceId]);

  const togglePin = useCallback(async (boardId: string): Promise<boolean> => {
    if (!userId) return false;
    const isPinned = await toggleBoardPin(boardId, userId);
    setPinnedIds(prev =>
      isPinned ? (prev.includes(boardId) ? prev : [...prev, boardId])
               : prev.filter(id => id !== boardId),
    );
    return isPinned;
  }, [userId]);

  /** 게시판 목록 새로고침 */
  const refreshBoards = useCallback(async () => {
    if (!activeWorkspaceId) return;
    try {
      setLoading(true);
      setError(null);
      const list = await fetchBoards(activeWorkspaceId);
      setBoards(list);
    } catch (err) {
      console.error('[BoardProvider] 게시판 목록 로드 실패:', err);
      setError(t`Failed to load boards`);
    } finally {
      setLoading(false);
    }
  }, [activeWorkspaceId]);

  /** 게시글 목록 새로고침 (검색 모드 지원) */
  const refreshPosts = useCallback(async (boardId: string, page?: number) => {
    try {
      setLoading(true);
      setError(null);
      if (searchQuery.trim()) {
        const result = await searchPostsService(boardId, searchQuery.trim(), { page: page ?? 0 });
        setPosts(result.posts);
        setTotalPosts(result.total);
      } else {
        const result = await fetchPosts(boardId, { page: page ?? 0 });
        setPosts(result.posts);
        setTotalPosts(result.total);
      }
    } catch (err) {
      console.error('[BoardProvider] 게시글 목록 로드 실패:', err);
      setError(t`Failed to load posts`);
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  // 워크스페이스 변경 시 게시판 목록 초기화. URL hub reset 은 '진짜 워크스페이스 전환' 일 때만 —
  // 첫 마운트(직접 URL 접속/새로고침) 에서는 URL 보존해야 boardId/postId 매칭 가능.
  const prevWsRef = useRef<string | null>(null);
  // 게시판 목록은 워크스페이스 진입 시 미리 로드 (채널 rooms 처럼 prefetch).
  // → 게시판 모듈 진입 시점엔 이미 boards 가 있어 '게시판 없음' 깜빡임이 없음.
  useEffect(() => {
    if (activeWorkspaceId) refreshBoards();
  }, [activeWorkspaceId, refreshBoards]);

  // 게시판 모듈 진입/워크스페이스 전환 시 선택·게시글 상태 reset + URL 정리 (fetch 는 위 prefetch 가 담당)
  useEffect(() => {
    if (activeWorkspaceId && activeModule === 'board') {
      const wsChanged = prevWsRef.current !== null && prevWsRef.current !== activeWorkspaceId;
      prevWsRef.current = activeWorkspaceId;
      setSelectedBoardRaw(null);
      setSelectedPostRaw(null);
      setPosts([]);
      setTotalPosts(0);
      setSearchQuery('');
      if (wsChanged && location.pathname.startsWith('/board/')) {
        navigate('/board', { replace: true });
      }
    }
  }, [activeWorkspaceId, activeModule]);

  useEffect(() => {
    if (selectedBoard) {
      refreshPosts(selectedBoard.id);
    }
  }, [selectedBoard?.id]);

  // URL → selectedBoard sync. boards 가 채워진 후 URL 의 boardId 매칭하여 자동 선택.
  // URL 이 hub 인데 보드가 있으면 첫 보드를 fallback 으로 navigate — LeftMenuBoard 의 자동 선택 로직 통합.
  // boards 가 refresh 되면 같은 boardId 라도 객체 reference 가 다를 수 있음(예: 설정에서 display_style 변경)
  // → reference 비교로 갱신해서 즉시 반영.
  useEffect(() => {
    if (urlIds.boardId) {
      const found = boards.find(b => b.id === urlIds.boardId) ?? null;
      if (found !== selectedBoard) {
        setSelectedBoardRaw(found);
      }
      // boards 아직 로드 중이면 다음 boards 변경 사이클에서 매칭. stale ID 면 found=null → 빈 화면.
    } else {
      // URL 이 /board (hub) — 자동으로 첫 보드로 보내지 않음. BoardPage 가 대시보드를 렌더.
      if (selectedBoard) setSelectedBoardRaw(null);
    }
  }, [urlIds.boardId, boards, activeModule]);

  // URL → selectedPost sync. posts 안에 있으면 그대로 쓰고, 없으면 fetchPost. 보드 미선택 시는 fetch 안 함.
  useEffect(() => {
    if (urlIds.postId === (selectedPost?.id ?? null)) return;
    if (!urlIds.postId) {
      setSelectedPostRaw(null);
      return;
    }
    if (!urlIds.boardId) return; // 보드 없는 post URL 은 무시
    const found = posts.find(p => p.id === urlIds.postId);
    if (found) {
      setSelectedPostRaw(found);
      return;
    }
    // posts 에 없는 경우 (직접 URL 접속 / 새로고침) 단건 fetch
    let canceled = false;
    fetchPost(urlIds.postId).then(p => {
      if (!canceled && p) setSelectedPostRaw(p);
    }).catch(() => {});
    return () => { canceled = true; };
  }, [urlIds.postId, urlIds.boardId, posts]);

  const contextValue = {
    boards,
    setBoards,
    selectedBoard,
    setSelectedBoard,
    selectedPost,
    setSelectedPost,
    posts,
    setPosts,
    totalPosts,
    setTotalPosts,
    loading,
    setLoading,
    refreshBoards,
    refreshPosts,
    searchQuery,
    setSearchQuery,
    pinnedIds,
    togglePin,
    error,
  };

  return (
    <BoardContext.Provider value={contextValue}>
      {children}
    </BoardContext.Provider>
  );
};

export default BoardProvider;
